import { FunctionComponent, useCallback, useMemo } from 'react';

import { NotesTable } from '~/client/components/shared/NotesTable';
import {
  useEntityNoteList,
  useReferenceListDisplayValue,
  useWorksOrdersList,
} from '~/client/hooks/storeHooks';
import { ClientGlobalStore } from '~/client/store/tinybase/ClientGlobalStore';
import { Note } from '~/common/types/notes';

type Props = {
  propertyId: number;
  serviceRequestId: number;
};

const makeGetNoteSubject =
  (refListDisplayValue: ClientGlobalStore['referenceListDisplayValue']) =>
  ({ entityRef, type, subtype, subject }: Note) => {
    if (type === 'WOR') {
      const subtypeDisplay = refListDisplayValue('NOTE_SUBTYPE', subtype);
      return `Works order ${entityRef}${subtypeDisplay ? ` - ${subtypeDisplay}` : ''}`;
    }
    return subject;
  };

export const ServiceRequestNotes: FunctionComponent<Props> = ({ propertyId, serviceRequestId }) => {
  const referenceListDisplayValue = useReferenceListDisplayValue();
  const worksOrders = useWorksOrdersList(serviceRequestId);
  const worksOrderIds = worksOrders.map((order) => order.worksOrderId);

  const serviceRequestNotes = useEntityNoteList('SRQ', serviceRequestId);
  const inspectionNotes = useEntityNoteList('INS', serviceRequestId);
  const worksOrderNotes = useEntityNoteList('WOR', worksOrderIds);

  // Memoise to ensure object equivalence in hook dep lists
  const allNotes = useMemo(
    () => [...serviceRequestNotes, ...inspectionNotes, ...worksOrderNotes],
    [serviceRequestNotes, inspectionNotes, worksOrderNotes],
  );

  const getNoteSubject = useCallback(makeGetNoteSubject(referenceListDisplayValue), [
    referenceListDisplayValue,
  ]);

  return (
    <NotesTable
      propertyId={propertyId}
      notes={allNotes}
      typeFilterOptions={['SRQ', 'INS', 'WOR']}
      getNoteSubject={getNoteSubject}
    />
  );
};
